<template>
    <div class="wishlist-container">
        <UserListSidebar :selected-user="currentUser" title="用户列表" @select-user="selectUser" />

        <div class="wishlist-content">
            <div class="no-user-selected" v-if="!currentUser">
                <div class="no-user-icon">👥</div>
                <div class="no-user-text">请选择左侧用户</div>
                <div class="no-user-subtitle">查看对应的心愿单</div>
            </div>

            <template v-else>
                <div class="wishlist-section">
                    <div class="section-header">
                        <h3 class="section-title">心愿单</h3>
                        <div class="color-gray2 ml-10">您最多可以在愿望清单中添加6件礼物</div>
                    </div>

                    <div class="wishlist-grid" v-if="wishList.length > 0">
                        <div class="wishlist-item" v-for="(item, index) in wishList" :key="item.idPresent">
                            <div class="item-image">
                                <img :src="item.imageUrl" :alt="item.name" class="gift-img">
                                <div class="remove-btn" @click="removeFromWishlist(item, index)">
                                    <i class="el-icon-close"></i>
                                </div>
                            </div>
                            <div class="item-info">
                                <div class="item-name">{{ item.name }}</div>
                                <div class="item-price" v-if="item.cost">{{ item.cost }} cr</div>
                            </div>
                        </div>
                    </div>

                    <div class="empty-wishlist" v-else>
                        <div class="empty-icon">💝</div>
                        <div class="empty-text">心愿单暂时为空</div>
                        <div class="empty-subtitle">快去下面选择喜欢的礼物吧</div>
                    </div>
                </div>

                <div class="category-nav-section" v-if="categoryList.length > 0">
                    <div class="category-nav-container">
                        <div class="category-nav-items">
                            <div v-for="category in categoryList" :key="category.id"
                                :class="['category-nav-item', { 'active': activeCategoryId === category.id }]"
                                :data-category-id="category.id" @click="scrollToCategory(category.id)">
                                <img :src="category.imageUrl" :alt="category.name" class="category-nav-icon">
                                <span>{{ category.name }}</span>
                                <div v-if="getWishlistCountByCategory(category.id) > 0" class="wishlist-badge-count">
                                    {{ getWishlistCountByCategory(category.id) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="gifts-section" @scroll="onScroll" ref="giftsScrollContainer">
                    <div class="category-section" v-for="category in categoryList" :key="category.id">
                        <div class="category-header" :id="'category-' + category.id">
                            <div class="category-info">
                                <img :src="category.imageUrl" :alt="category.name" class="category-icon">
                                <span class="category-name">{{ category.name }}</span>
                                <span class="category-count">({{ getGiftsByCategory(category.id).length }})</span>
                            </div>
                        </div>

                        <div class="gifts-grid">
                            <div class="gift-item" v-for="(item, index) in getGiftsByCategory(category.id)"
                                :key="item.id">
                                <div v-if="isInWishlist(item.id)"
                                    class="wishlist-badge flex row middle fs-12 color-fff">
                                    <img src="@/assets/svg/heart.svg" alt="" class="w-16 h-16">
                                    已在心愿单
                                </div>
                                <div class="gift-image">
                                    <img :src="item.imageUrl" :alt="item.name" class="gift-img">
                                </div>
                                <div class="gift-info">
                                    <div class="gift-name">{{ item.name }}</div>
                                    <div class="gift-price" v-if="item.cost">{{ item.cost }} cr</div>
                                </div>
                                <div class="gift-actions">
                                    <!-- 根据心愿单状态显示不同按钮 -->
                                    <el-button v-if="!isInWishlist(item.id)" type="primary" size="mini"
                                        @click="addToWishlist(item)" class="add-wishlist-button">
                                        加入心愿单
                                    </el-button>
                                    <el-button v-else type="danger" size="mini" @click="removeFromWishlistById(item.id)"
                                        class="remove-wishlist-button">
                                        移出心愿单
                                    </el-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- 加载状态 -->
        <div class="loading-container" v-if="loading">
            <img class="loading-img rotate360" src="@/assets/loading.png" alt="加载中...">
        </div>
    </div>
</template>

<script>
import {
    getPresentList,
    getWishList,
    updateWishList
} from '@/api/talkytimes/user'
import UserListSidebar from '@/components/UserListSidebar.vue'

export default {
    name: 'WishList',
    components: {
        UserListSidebar
    },
    data() {
        return {
            currentUser: null, // 当前选择的用户
            wishList: [], // 当前用户心愿单商品
            presentList: [], // 所有礼物
            categoryList: [], // 分类列表
            activeCategoryId: null, // 当前激活的分类ID
            loading: false
        }
    },
    methods: {
        // 移除 initData 方法，改为使用 UserListSidebar 组件自动获取数据

        // 加载礼物列表，所有礼物都一样，写死id以便快速请求
        async loadPresentList() {
            try {
                const presentListRes = await getPresentList({
                    cursor: "",
                    idRegularUser: 69593048,
                    limit: 30
                });

                this.presentList = presentListRes.items || [];

                // 获取礼物列表后，处理分类
                this.getCategoryList();

                // 设置第一个分类为激活状态
                if (this.categoryList.length > 0) {
                    this.activeCategoryId = this.categoryList[0].id;
                }
            } catch (error) {
                console.error('加载礼物列表失败:', error);
                this.$message.error('加载礼物列表失败！');
            }
        },

        // 选择用户
        async selectUser(user) {
            if (this.currentUser && this.currentUser.id === user.id) {
                return; // 已经是当前用户，无需重复选择
            }

            this.currentUser = user;
            this.loading = true;

            try {
                // 设置当前用户的token
                localStorage.setItem('activeUser', JSON.stringify(user));

                // 每次都重新获取该用户的心愿单
                const wishListRes = await getWishList();
                this.wishList = wishListRes || [];
            } catch (error) {
                console.error('获取用户心愿单失败:', error);
                this.$message.error('获取心愿单失败！');
                this.wishList = [];
            } finally {
                this.loading = false;
            }
        },

        // 获取分类列表
        getCategoryList() {
            const categories = new Map();
            this.presentList.forEach(item => {
                if (item.category && !categories.has(item.category.id)) {
                    categories.set(item.category.id, {
                        ...item.category,
                        gifts: []
                    });
                }
            });

            // 转换为数组并按优先级排序
            this.categoryList = Array.from(categories.values())
                .sort((a, b) => a.priority - b.priority);
        },

        // 根据分类ID获取礼物列表
        getGiftsByCategory(categoryId) {
            return this.presentList
                .filter(item => item.category && item.category.id === categoryId)
                .sort((a, b) => a.priority - b.priority);
        },

        // 滚动到指定分类
        scrollToCategory(categoryId) {
            const element = document.getElementById(`category-${categoryId}`);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        },

        // 监听滚动，更新激活的分类
        onScroll() {
            if (!this.categoryList.length) {
                return;
            }

            const container = this.$refs.giftsScrollContainer;
            if (!container) {
                return;
            }

            const scrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;

            // 遍历所有分类，找到当前可见的分类
            for (let i = this.categoryList.length - 1; i >= 0; i--) {
                const category = this.categoryList[i];
                const element = document.getElementById(`category-${category.id}`);
                if (element) {
                    const elementTop = element.offsetTop;

                    // 如果分类标题在可视区域内，则激活该分类
                    if (scrollTop + 100 <= elementTop && scrollTop + containerHeight >= elementTop) {
                        if (this.activeCategoryId !== category.id) {
                            this.activeCategoryId = category.id;
                        }
                        break;
                    }
                }
            }
        },

        // 移出心愿单
        removeFromWishlist(item, index) {
            if (!this.currentUser) return;

            this.$confirm(`确定要将 "${item.name}" 移出心愿单吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                // 构建新的心愿单ID数组（移除指定礼物）
                const newWishListIds = this.wishList
                    .filter((wishItem, i) => i !== index) // 排除要移除的礼物
                    .map(wishItem => wishItem.idPresent); // 提取ID

                // 调用API更新心愿单
                updateWishList({
                    idsPresents: newWishListIds
                }).then(res => {
                    this.$message.success('移出心愿单成功！');
                    // 重新获取心愿单数据
                    this.updateCurrentUserWishList();
                }).catch(error => {
                    console.error('移出心愿单失败:', error);
                    this.$message.error('移出心愿单失败！');
                });
            })
        },

        // 加入心愿单
        addToWishlist(item) {
            if (!this.currentUser) return;

            this.$confirm(`确定要将 "${item.name}" 加入心愿单吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                // 构建新的心愿单ID数组（添加新礼物）
                const currentWishListIds = this.wishList.map(wishItem => wishItem.idPresent);
                const newWishListIds = [...currentWishListIds, item.id];

                // 调用API更新心愿单
                updateWishList({
                    idsPresents: newWishListIds
                }).then(res => {
                    this.$message.success('加入心愿单成功！');
                    // 重新获取心愿单数据
                    this.updateCurrentUserWishList();
                }).catch(error => {
                    console.error('加入心愿单失败:', error);
                    this.$message.error('加入心愿单失败！');
                });
            })
        },

        // 更新当前用户心愿单列表
        updateCurrentUserWishList() {
            if (!this.currentUser) return;

            getWishList().then(res => {
                this.wishList = res || [];
            })
        },

        // 检查礼物是否已在心愿单中
        isInWishlist(giftId) {
            return this.wishList.some(wishItem => wishItem.idPresent === giftId);
        },

        // 通过礼物ID移出心愿单
        removeFromWishlistById(giftId) {
            const wishItem = this.wishList.find(item => item.idPresent === giftId);
            if (wishItem) {
                this.removeFromWishlist(wishItem, this.wishList.findIndex(item => item.idPresent === giftId));
            }
        },

        // 滚动分类导航到可视区域
        scrollCategoryNavToView(categoryId) {
            const categoryNavContainer = document.querySelector('.category-nav-items');
            if (!categoryNavContainer) return;

            const targetItem = categoryNavContainer.querySelector(`[data-category-id="${categoryId}"]`);
            if (!targetItem) return;

            // 计算目标元素相对于容器的位置
            const containerRect = categoryNavContainer.getBoundingClientRect();
            const targetRect = targetItem.getBoundingClientRect();

            // 计算需要滚动的距离
            const scrollLeft = targetItem.offsetLeft - (containerRect.width / 2) + (targetItem.offsetWidth / 2);

            // 平滑滚动到目标位置
            categoryNavContainer.scrollTo({
                left: Math.max(0, scrollLeft),
                behavior: 'smooth'
            });
        },

        // 获取指定分类下在心愿单中的礼物数量
        getWishlistCountByCategory(categoryId) {
            if (!this.wishList || !this.presentList) return 0;

            // 获取该分类下的所有礼物ID
            const categoryGiftIds = this.presentList
                .filter(item => item.category && item.category.id === categoryId)
                .map(item => item.id);

            // 统计在心愿单中的数量
            return this.wishList.filter(wishItem =>
                categoryGiftIds.includes(wishItem.idPresent)
            ).length;
        }
    },

    watch: {
        activeCategoryId: {
            handler(newVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.scrollCategoryNavToView(newVal);
                    });
                }
            },
            immediate: false
        }
    },

    mounted() {
        // 移除 initData 调用，让 UserListSidebar 组件自动获取数据
        this.loadPresentList();
    },
    computed: {
        // 获取用户列表，与 UserListSidebar 组件使用相同的数据源
        userList() {
            return this.$store.state.accountList.accountList || [];
        }
    },
    watch: {
        // 监听用户列表变化，当有数据时自动选择第一个用户
        userList: {
            handler(newUserList) {
                if (newUserList.length > 0 && !this.currentUser) {
                    console.log('WishList page - Auto selecting first user:', newUserList[0]);
                    this.selectUser(newUserList[0]);
                }
            },
            immediate: true
        }
    },
}
</script>

<style lang="scss" scoped>
.wishlist-container {
    display: flex;
    height: calc(100vh - 56px);
    background-color: #f5f7fa;
}

// 左侧用户列表
.user-list-section {
    width: 300px;
    border-right: 1px solid #e4e7ed;
    display: flex;
    flex-direction: column;

    .user-list-content {
        flex: 1;
        overflow-y: auto;
        padding: 10px;

        .user-item {
            display: flex;
            align-items: center;
            background: #fff;
            padding: 12px;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-bottom: 5px;
            position: relative;

            &:hover {
                background-color: #f8f9fa;
            }

            &.active {
                background-color: #f0f0ff;
                border: 1px solid #2929c7;
            }

            .user-avatar {
                position: relative;
                margin-right: 12px;
                flex-shrink: 0;

                .avatar-img {
                    width: 45px;
                    height: 45px;
                    border-radius: 50%;
                    object-fit: cover;
                }

                .online-status {
                    position: absolute;
                    bottom: 2px;
                    right: 2px;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background-color: #ccc;
                    border: 2px solid #fff;

                    &.online {
                        background-color: #0CD85D;
                    }
                }
            }

            .user-info {
                flex: 1;
                min-width: 0;

                .user-name {
                    font-size: 14px;
                    font-weight: 500;
                    color: #333;
                    margin-bottom: 4px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .user-status {
                    font-size: 12px;
                    color: #666;
                }
            }
        }
    }
}

// 右侧心愿单内容
.wishlist-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0; // 允许flex子项缩小
}

// 未选择用户状态
.no-user-selected {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #999;

    .no-user-icon {
        font-size: 64px;
        line-height: 1;
        margin-bottom: 20px;
    }

    .no-user-text {
        font-size: 20px;
        color: #666;
        margin-bottom: 8px;
    }

    .no-user-subtitle {
        font-size: 14px;
        color: #999;
    }
}

// 心愿单区域样式
.wishlist-section {
    background: #fff;
    margin: 15px 20px 10px 20px;
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0; // 防止被压缩

    .section-header {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid #e4e7ed;

        .section-title {
            font-size: 18px;
            font-weight: 600;
            color: #2929c7;
            margin: 0;
        }
    }

    .wishlist-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: 15px;

        .wishlist-item {
            position: relative;
            background: #fff;
            border-radius: 8px;
            padding: 12px;
            border: 1px solid #e4e7ed;
            transition: all 0.3s ease;

            &:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                transform: translateY(-2px);
            }

            .item-image {
                position: relative;
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                margin-bottom: 10px;

                .gift-img {
                    width: 70px;
                    height: 70px;
                    border-radius: 8px;
                    object-fit: cover;
                }

                .remove-btn {
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    width: 20px;
                    height: 20px;
                    background: #f56c6c;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #fff;
                    font-size: 10px;
                    transition: all 0.3s ease;

                    &:hover {
                        background: #e6393f;
                        transform: scale(1.1);
                    }
                }
            }

            .item-info {
                text-align: center;

                .item-name {
                    font-size: 12px;
                    color: #333;
                    margin-bottom: 5px;
                    font-weight: 500;
                    line-height: 1.3;
                    height: 2.6em; // 显示两行文本
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    word-break: break-word;
                }

                .item-price {
                    font-size: 10px;
                    color: #2929c7;
                    font-weight: 600;
                }
            }
        }
    }

    .empty-wishlist {
        text-align: center;
        padding: 30px 20px;
        color: #999;

        .empty-icon {
            font-size: 40px;
            margin-bottom: 15px;
        }

        .empty-text {
            font-size: 16px;
            color: #666;
            margin-bottom: 8px;
        }

        .empty-subtitle {
            font-size: 12px;
            color: #999;
        }
    }
}

// 分类导航区域
.category-nav-section {
    background: #fff;
    margin: 0 20px 10px 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0; // 防止被压缩

    .category-nav-container {
        padding: 12px;

        .category-nav-items {
            display: flex;
            overflow-x: auto;
            gap: 12px;
            padding: 8px;

            // 滚动条样式
            &::-webkit-scrollbar {
                height: 4px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 2px;
            }

            &::-webkit-scrollbar-thumb {
                background: #c1c1c1;
                border-radius: 2px;

                &:hover {
                    background: #a8a8a8;
                }
            }

            .category-nav-item {
                display: flex;
                flex-direction: row;
                align-items: center;
                padding: 8px 12px;
                border-radius: 25px;
                cursor: pointer;
                transition: all 0.3s ease;
                border: 2px solid transparent;
                flex-shrink: 0;
                white-space: nowrap;
                color: #333;
                position: relative;
                background: #f8f9fa;

                &:hover {
                    background-color: #2929c7;
                    color: #fff;
                }

                &.active {
                    background-color: #f0f0ff;
                    border-color: #2929c7;
                    font-weight: bold;
                    color: #2929c7;
                }

                .category-nav-icon {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    margin-right: 6px;
                    object-fit: cover;
                }

                span {
                    font-size: 12px;
                    white-space: nowrap;
                }

                // 心愿单数量角标样式
                .wishlist-badge-count {
                    position: absolute;
                    top: -3px;
                    right: -3px;
                    background: #2929c7;
                    color: #fff;
                    border-radius: 50%;
                    width: 16px;
                    height: 16px;
                    font-size: 9px;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                    line-height: 1;
                }
            }
        }
    }
}

// 商品分类区域
.gifts-section {
    flex: 1;
    overflow-y: auto;
    background: #fff;
    margin: 0 20px 15px 20px;
    border-radius: 12px;
    padding: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    min-height: 0; // 允许flex子项缩小

    .category-section {
        margin-bottom: 30px;

        &:last-child {
            margin-bottom: 0;
        }

        .category-header {
            background: #f5f7fa;
            padding: 12px 15px;
            margin-bottom: 12px;
            border-left: 4px solid #2929c7;
            border-radius: 0 8px 8px 0;
            scroll-margin-top: 60px;

            .category-info {
                display: flex;
                align-items: center;
                gap: 12px;

                .category-icon {
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    object-fit: cover;
                }

                .category-name {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }

                .category-count {
                    font-size: 12px;
                    color: #666;
                }
            }
        }

        .gifts-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 15px;

            .gift-item {
                background: #fff;
                border: 1px solid #e4e7ed;
                border-radius: 12px;
                padding: 22px 12px 12px 12px;
                transition: all 0.3s ease;
                position: relative;

                &:hover {
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
                    transform: translateY(-3px);
                }

                .wishlist-badge {
                    position: absolute;
                    top: 5px;
                    left: 5px;
                    background: #2929c7;
                    border-radius: 50px;
                    padding: 3px 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                    z-index: 1;
                }

                .gift-image {
                    position: relative;
                    display: flex;
                    flex-flow: row nowrap;
                    justify-content: center;
                    margin-bottom: 10px;

                    .gift-img {
                        width: 80px;
                        height: 80px;
                        border-radius: 10px;
                        object-fit: cover;
                    }
                }

                .gift-info {
                    text-align: center;
                    margin-bottom: 10px;

                    .gift-name {
                        font-size: 14px;
                        color: #333;
                        font-weight: 500;
                        margin-bottom: 5px;
                        line-height: 1.3;
                        height: 2.6em; // 显示两行文本
                        overflow: hidden;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        word-break: break-word;
                    }

                    .gift-price {
                        font-size: 12px;
                        color: #2929c7;
                        font-weight: 600;
                        margin-bottom: 4px;
                    }
                }

                .gift-actions {
                    text-align: center;

                    .add-wishlist-button,
                    .remove-wishlist-button {
                        width: 100%;
                        border-radius: 6px !important;
                        font-size: 11px !important;
                        padding: 6px 12px !important;
                        font-weight: 500 !important;
                        transition: all 0.3s ease !important;
                    }

                    .add-wishlist-button {
                        background-color: #2929c7 !important;
                        border-color: #2929c7 !important;
                        color: #fff !important;

                        &:hover {
                            background-color: #1a1a99 !important;
                            border-color: #1a1a99 !important;
                            transform: translateY(-1px);
                        }
                    }

                    .remove-wishlist-button {
                        background-color: #f56c6c !important;
                        border-color: #f56c6c !important;
                        color: #fff !important;

                        &:hover {
                            background-color: #e6393f !important;
                            border-color: #e6393f !important;
                            transform: translateY(-1px);
                        }
                    }
                }
            }
        }
    }
}

// 加载状态
.loading-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;

    .loading-img {
        width: 40px;
        height: 40px;
    }
}

// 旋转动画
.rotate360 {
    animation: rotate 2s linear infinite;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.el-loading-mask {
    background-color: transparent;
}
</style>